import {
  CREDIT_CARD_SUBTYPE_KEYS,
  DEPOSITORY_SUBTYPE_KEYS,
  INVESTMENT_SUBTYPE_KEYS,
} from '@orn/global/financialAccounts/supportedAccountTypes.js';

import { FinancialAccountPrimaryType, FinancialAccountSubtype } from '../../__generated__/globalTypes';
import { firstLetterCapitalize } from '../formatting';

export interface IAccountTypeCheck {
  type: FinancialAccountPrimaryType;
  /** Absent when the institution reports none; such an account is unsupported. */
  subtype?: FinancialAccountSubtype | null;
}

/** Whether the household's investment accounts are in scope for the caller. */
export interface ISupportedFinancialAccountOptions {
  includeInvestments: boolean;
}

// type-graphql exposes the core enum's key names over the wire, so this app's
// codegen'd `FinancialAccountSubtype` *values* equal the @orn/global key strings
// (`Checking = 'CHECKING'`). Resolve the shared keys to enum values via lookup —
// no cast; the sibling unit test pins the invariant so a codegen change can't
// silently empty the supported set.
function toSubtype(key: string): FinancialAccountSubtype | undefined {
  return Object.values(FinancialAccountSubtype).find((value) => String(value) === key);
}

function toSubtypeSet(keys: readonly string[]): ReadonlySet<FinancialAccountSubtype> {
  return new Set(keys.map(toSubtype).filter((value): value is FinancialAccountSubtype => value !== undefined));
}

const DEPOSITORY_SUBTYPES = toSubtypeSet(DEPOSITORY_SUBTYPE_KEYS);
const CREDIT_CARD_SUBTYPES = toSubtypeSet(CREDIT_CARD_SUBTYPE_KEYS);
const INVESTMENT_SUBTYPES = toSubtypeSet(INVESTMENT_SUBTYPE_KEYS);

function hasSubtypeIn(account: IAccountTypeCheck, subtypes: ReadonlySet<FinancialAccountSubtype>): boolean {
  return account.subtype != null && subtypes.has(account.subtype);
}

export function isDepositoryAccount(account: IAccountTypeCheck): boolean {
  return account.type === FinancialAccountPrimaryType.Depository && hasSubtypeIn(account, DEPOSITORY_SUBTYPES);
}

export function isCreditCardAccount(account: IAccountTypeCheck): boolean {
  return account.type === FinancialAccountPrimaryType.Credit && hasSubtypeIn(account, CREDIT_CARD_SUBTYPES);
}

// The primary type is part of the check: Plaid files `hsa` under both
// depository and investment.
export function isInvestmentAccount(account: IAccountTypeCheck): boolean {
  return account.type === FinancialAccountPrimaryType.Investment && hasSubtypeIn(account, INVESTMENT_SUBTYPES);
}

export interface IAccountAsOfSource {
  lastSyncedAt?: string | null;
}

/**
 * When the account's displayed balance was last known good, or null when it has never synced:
 * the last time the provider reported this account, so one it stopped reporting falls behind its
 * siblings. `readHouseholdNetWorth` (`@orn/core`) dates the household total by this rule, also
 * counting a balance read after the last sync; change both.
 */
export function accountAsOf(account: IAccountAsOfSource): string | null {
  return account.lastSyncedAt ?? null;
}

/**
 * Whether the app displays this account in a section; every other account lands in
 * "Unsupported accounts" on the Accounts page. The option is required so no call site
 * reads as investments-off by omission.
 */
export function isSupportedFinancialAccount(
  account: IAccountTypeCheck,
  { includeInvestments }: ISupportedFinancialAccountOptions
): boolean {
  return (
    isDepositoryAccount(account) || isCreditCardAccount(account) || (includeInvestments && isInvestmentAccount(account))
  );
}

/** The per-account notification threshold an account type supports, if any. */
export type ThresholdKind = 'lowBalance' | 'creditUtilization';

/**
 * Which per-account alert threshold applies to an account: a depository account
 * has a low-balance ($) threshold, a credit card a utilization (%) threshold,
 * and every other type none.
 */
export function getThresholdKind(account: IAccountTypeCheck): ThresholdKind | null {
  if (isDepositoryAccount(account)) {
    return 'lowBalance';
  }
  if (isCreditCardAccount(account)) {
    return 'creditUtilization';
  }
  return null;
}

/**
 * Returns a singular display name for account subtypes.
 * Used for individual account display (e.g., "Credit Card").
 */
export const formatAccountSubtype = (subtype: FinancialAccountSubtype): string => {
  const subtypeMap: Partial<Record<FinancialAccountSubtype, string>> = {
    [FinancialAccountSubtype.Checking]: 'Checking',
    [FinancialAccountSubtype.Savings]: 'Savings',
    [FinancialAccountSubtype.CreditCard]: 'Credit Card',
    [FinancialAccountSubtype.MoneyMarket]: 'Money Market',
    [FinancialAccountSubtype.Cd]: 'CD',
    [FinancialAccountSubtype.Brokerage]: 'Brokerage',
    // Plaid has no rollover subtype, so a rollover IRA reads as traditional too.
    [FinancialAccountSubtype.Ira]: 'Traditional IRA',
    [FinancialAccountSubtype.Roth]: 'Roth IRA',
    [FinancialAccountSubtype.SepIra]: 'SEP IRA',
    [FinancialAccountSubtype.SimpleIra]: 'SIMPLE IRA',
    [FinancialAccountSubtype.Plan_401K]: '401(k)',
    [FinancialAccountSubtype.Plan_401A]: '401(a)',
    [FinancialAccountSubtype.Plan_403B]: '403(b)',
    [FinancialAccountSubtype.Plan_457B]: '457(b)',
    [FinancialAccountSubtype.Roth_401K]: 'Roth 401(k)',
    [FinancialAccountSubtype.Pension]: 'Pension',
    [FinancialAccountSubtype.Hsa]: 'HSA',
    [FinancialAccountSubtype.Plan_529]: '529 Plan',
    [FinancialAccountSubtype.CryptoExchange]: 'Crypto Exchange',
    [FinancialAccountSubtype.Trust]: 'Trust',
    [FinancialAccountSubtype.Ugma]: 'UGMA',
    [FinancialAccountSubtype.Utma]: 'UTMA',
    [FinancialAccountSubtype.Loan]: 'Loan',
    [FinancialAccountSubtype.Mortgage]: 'Mortgage',
    [FinancialAccountSubtype.Auto]: 'Auto Loan',
    [FinancialAccountSubtype.Student]: 'Student Loan',
    [FinancialAccountSubtype.HomeEquity]: 'Home Equity Loan',
    [FinancialAccountSubtype.LineOfCredit]: 'Line of Credit',
    [FinancialAccountSubtype.Paypal]: 'PayPal',
    [FinancialAccountSubtype.Prepaid]: 'Prepaid',
    [FinancialAccountSubtype.Other]: 'Other Account',
  };

  return subtypeMap[subtype] || firstLetterCapitalize(subtype.replace(/_/g, ' '));
};
